import { useState, useEffect } from "react";
import { 
    Network, Clock, Search, ArrowRightLeft, 
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ChevronUp, ChevronDown, Monitor
} from "lucide-react";

import { showError } from "../../../../utils/toast"; // Adjust import path
import baseapi from "../../../../services/baseapi"; // Adjust import path
import { ItemsPerPageDropdown } from "../../../../components/ItemsPerPageDropdown ";

interface BridgeEntry {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    agent_id: number;
    client_id: number;
    organization_id: number;
    asset_id: number;
    mac_address: string;
    interface_name: string;
    entry_type: string;
    tx_bytes: number;
    manufacturer: string | null;
}

interface ApBridgeTableProps {
    assetId: number;
    client_id: string;
}

export default function ApBridgeTable({ assetId, client_id }: ApBridgeTableProps) {
    const [data, setData] = useState<BridgeEntry[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search and Sort state
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [sortField, setSortField] = useState<string>('mac_address');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Debounce search input to avoid spamming the API
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            if (searchQuery !== debouncedSearch) {
                setCurrentPage(1); // Reset to page 1 only when search actually changes
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery, debouncedSearch]);

    const fetchBridgeData = async () => {
        if (!assetId) return;
        
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            let queryObj:any = {}
            // Build API parameters
            const params: any = {
                skip,
                limit,
                sort_by: sortField,
                sort_order: sortOrder
            };

            queryObj.asset_id = assetId
            queryObj.client_id = client_id

            // Add search parameter if it exists
            if (debouncedSearch.trim()) {
                queryObj.mac_address = {$ilike: `%${debouncedSearch.trim()}%`};
            }

            params.query = JSON.stringify(queryObj)

            const response = await baseapi.get("/ap_bridge_table", { params });

            if (response.data.status) {
                setData(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length);
            } else {
                showError(response.data.message || "Failed to load bridge table data");
                setData([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "An error occurred while fetching bridge data");
            setData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // Re-fetch when dependencies change
    useEffect(() => {
        fetchBridgeData();
    }, [assetId, currentPage, limit, debouncedSearch, sortField, sortOrder]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    const renderSortIcon = (field: string) => {
        if (sortField !== field) return null;
        return (
            <div className="text-indigo-400 ml-1">
                {sortOrder === 'asc' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
            </div>
        );
    };

    return (
        <div className="w-full flex flex-col gap-3 text-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-500/10 p-1.5 rounded-md border border-indigo-500/20">
                        <Network size={16} className="text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white tracking-wide leading-tight">Bridge Table</h2>
                        <p className="text-[9px] text-gray-200 uppercase tracking-widest mt-0.5">MAC Address Forwarding</p>
                    </div>
                </div>
                
                <div className="flex items-center gap-3">
                    {/* Search Input */}
                    <div className="relative">
                        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-200" />
                        <input 
                            type="text" 
                            placeholder="Search MAC." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-gray-900 border border-gray-700 text-[10px] rounded-md pl-7 pr-3 py-1.5 focus:outline-none focus:border-indigo-500 text-gray-200 w-48 transition-colors placeholder:text-gray-600"
                        />
                    </div>

                    {/* Total Entries Badge */}
                    <div className="text-[9px] font-bold text-gray-300 bg-gray-800/40 px-2.5 py-1.5 rounded-md border border-gray-700">
                        Total Entries: <span className="text-white">{totalRecords}</span>
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-lg overflow-hidden shadow-sm h-fit w-full">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-800/80 border-b border-gray-700/50">
                            <tr>
                                <th 
                                    className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors select-none"
                                    onClick={() => handleSort('mac_address')}
                                >
                                    <div className="flex items-center gap-1.5">
                                        MAC Address
                                        {renderSortIcon('mac_address')}
                                    </div>
                                </th>
                                <th 
                                    className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors select-none"
                                    onClick={() => handleSort('interface_name')}
                                >
                                    <div className="flex items-center gap-1.5">
                                        Interface
                                        {renderSortIcon('interface_name')}
                                    </div>
                                </th>
                                <th 
                                    className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors select-none"
                                    onClick={() => handleSort('entry_type')}
                                >
                                    <div className="flex items-center gap-1.5">
                                        Type
                                        {renderSortIcon('entry_type')}
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        <Monitor size={12} className="text-blue-400" />
                                        Manufacturer
                                    </div>
                                </th>
                                <th 
                                    className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap cursor-pointer hover:bg-gray-700/50 transition-colors select-none text-right"
                                    onClick={() => handleSort('tx_bytes')}
                                >
                                    <div className="flex items-center justify-end gap-1.5">
                                        <ArrowRightLeft size={12} className="text-amber-400" />
                                        TX Bytes
                                        {renderSortIcon('tx_bytes')}
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap text-right">
                                    <div className="flex items-center justify-end gap-1.5">
                                        <Clock size={12} className="text-emerald-400" /> 
                                        Last Updated
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                            {loading && data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-8 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="w-5 h-5 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                            <p className="text-[9px] font-black text-gray-200 uppercase tracking-[0.2em]">Loading Bridge Table...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-8 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                            <Search size={20} className="text-gray-200" />
                                            <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">
                                                {searchQuery ? "No results match your search" : "No bridge entries found"}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-gray-800/40 transition-colors group">
                                        <td className="px-3 py-2">
                                            <span className="text-[11px] font-mono font-bold text-indigo-300">
                                                {entry.mac_address}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] font-mono text-gray-300 bg-gray-800 px-1.5 py-0.5 rounded border border-gray-700">
                                                {entry.interface_name}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 border text-[9px] font-bold uppercase tracking-wider rounded ${
                                                entry.entry_type === 'Own' 
                                                    ? 'bg-purple-500/10 border-purple-500/20 text-purple-400' 
                                                    : 'bg-blue-500/10 border-blue-500/20 text-blue-400'
                                            }`}>
                                                {entry.entry_type}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] text-gray-200 truncate max-w-[200px] block" title={entry.manufacturer || "Unknown"}>
                                                {entry.manufacturer || "Unknown"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <span className="text-[10px] font-mono font-medium text-amber-300">
                                                {entry.tx_bytes.toLocaleString()}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <span className="text-[10px] font-medium text-gray-200">
                                                {new Date(entry.updated + "Z").toLocaleString()}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {totalRecords > 0 && (
                    <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/60 border-t border-gray-700">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={limit}
                                onChange={(v) => {
                                    setLimit(v);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[9px] font-medium text-gray-200 tracking-widest">
                                <span className="text-gray-300">
                                    {(currentPage - 1) * limit + 1}
                                </span>
                                {" - "}
                                <span className="text-gray-300">
                                    {Math.min(currentPage * limit, totalRecords)}
                                </span>
                                <span className="mx-1 text-gray-600">/</span>
                                <span className="text-gray-300 font-bold">{totalRecords}</span>
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronsLeft size={12} />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronLeft size={12} />
                            </button>
                            <div className="px-1.5 py-0.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded text-[9px] font-bold mx-1">
                                {currentPage}
                            </div>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronRight size={12} />
                            </button>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronsRight size={12} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}